import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Modal, Select } from 'antd';

import CategorySelect from '../discover/CategorySelect';
import { InputIconSelect } from '../Input';
import { apiErrorMessage } from '../../utils/apiErrors';
import { patchPublisherPackage, publishingRefusal } from '../../services/publishing';

const sameList = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);

/**
 * A listing's keywords (keepr-api KPR-1431): `keywords`, or — from an API
 * before it — `tags` when they are words. A package's DETAIL `tags` are the
 * keepr tags it installs (objects), never its keywords.
 */
export function keywordsOf(pkg) {
	if (Array.isArray(pkg?.keywords)) return pkg.keywords;
	return Array.isArray(pkg?.tags) && pkg.tags.every(t => typeof t === 'string') ? pkg.tags : [];
}

/** The Good for box's text as its lines: one job a line, blanks dropped. */
export const goodForLines = text => String(text || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean);

/**
 * Only what changed: on a package ever listed, every listing field sent is a
 * new version for a reviewer (keepr-api KPR-1277, ruling Q3), so a field left
 * as it was is never sent. `{}` when nothing changed.
 */
export function listingChanges(pkg, values) {
	const out = {};
	const text = key => (values[key] === undefined ? undefined : String(values[key] ?? '').trim());
	['name', 'description', 'category'].forEach((key) => {
		const next = text(key);
		if (next !== undefined && next !== String(pkg?.[key] ?? '')) out[key] = next;
	});
	const keywords = Array.isArray(values.keywords) ? values.keywords.map(t => String(t).trim()).filter(Boolean) : undefined;
	if (keywords && !sameList(keywords, keywordsOf(pkg))) out.keywords = keywords;
	const goodFor = values.goodFor === undefined ? undefined : goodForLines(values.goodFor);
	if (goodFor && !sameList(goodFor, pkg?.goodFor)) out.goodFor = goodFor;
	if (values.icon !== undefined && (values.icon || '') !== (pkg?.icon || '')) out.icon = values.icon || '';
	if (values.color !== undefined && (values.color || '') !== (pkg?.color || '')) out.color = values.color || '';
	return out;
}

/**
 * Edit listing… on a publisher's package (keepr-api KPR-1331): its words on
 * Discover — name, description, category, keywords and Good for lines
 * (keepr-api KPR-1431) — and its icon and colour. A
 * draft's change applies at once; on a package ever listed it is sent for
 * review (`onSaved({ held })`), and Discover keeps the listing it has until a
 * reviewer approves it. A save finishes the dialog (platform-standards §8); a
 * refusal keeps it open with the reason.
 */
export default function EditListingModal({ pkg, onClose, onSaved }) {
	const [form] = Form.useForm();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const [identity, setIdentity] = useState({ icon: '', color: '' });

	useEffect(() => {
		if (!pkg) return;
		setError(null);
		setIdentity({ icon: pkg.icon || '', color: pkg.color || '' });
		form.setFieldsValue({ name: pkg.name, description: pkg.description || '', category: pkg.category || undefined, keywords: keywordsOf(pkg), goodFor: (pkg.goodFor || []).join('\n') });
	}, [pkg, form]);

	const submit = async () => {
		const values = await form.validateFields();
		const changes = listingChanges(pkg, { ...values, ...identity });
		if (!Object.keys(changes).length) {
			onClose?.();
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const answer = await patchPublisherPackage(pkg._id, changes, pkg.rev);
			onSaved?.(answer);
		} catch (e) {
			setError(publishingRefusal(e) || apiErrorMessage(e, 'The listing could not be saved.'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={Boolean(pkg)}
			title="Edit listing"
			okText="Save listing"
			onOk={submit}
			onCancel={onClose}
			okButtonProps={{ loading: busy }}
			destroyOnHidden
		>
			{pkg && pkg.status !== 'draft' ? (
				<p className="publishing-modal__note">A listed package&apos;s new listing is sent for review. Discover keeps showing this one until a reviewer approves it.</p>
			) : null}
			<Form form={form} layout="vertical" requiredMark={false}>
				<Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'A name is required.' }]}>
					<Input maxLength={100} aria-label="Name" />
				</Form.Item>
				<Form.Item name="description" label="Description" extra="What it is for and what it holds. Markdown.">
					<Input.TextArea autoSize={{ minRows: 4, maxRows: 12 }} maxLength={4000} aria-label="Description" />
				</Form.Item>
				<Form.Item name="category" label="Category" extra="One of Discover's categories.">
					<CategorySelect aria-label="Category" />
				</Form.Item>
				<Form.Item name="keywords" label="Keywords" extra="Up to 12 words people might search for that the name does not say, such as another name for it or the tool it replaces.">
					<Select mode="tags" tokenSeparators={[',']} maxCount={12} aria-label="Keywords" />
				</Form.Item>
				<Form.Item
					name="goodFor"
					label="Good for"
					extra="Up to 5 jobs it does, one a line, such as “Track every oil change and what it cost”."
					rules={[{ validator: (_, value) => (goodForLines(value).length > 5 ? Promise.reject(new Error('At most 5 lines.')) : goodForLines(value).some(line => [...line].length > 120) ? Promise.reject(new Error('Each line is at most 120 characters.')) : Promise.resolve()) }]}
				>
					<Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} aria-label="Good for" />
				</Form.Item>
			</Form>
			<InputIconSelect
				elementName="icon"
				elementLabel="Icon and color"
				value={identity.icon || null}
				onChange={(_, icon) => setIdentity(current => ({ ...current, icon: icon || '' }))}
				colorValue={identity.color || null}
				onColorChange={(_, color) => setIdentity(current => ({ ...current, color: color || '' }))}
				recordKind={pkg?.kind === 'collection' ? 'collection' : 'card'}
			/>
			{error ? <Alert type="error" showIcon message={error} className="publishing-modal__error" /> : null}
		</Modal>
	);
}
